import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Check, X, FileText, Calendar, User, Inbox, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { cn } from '@/lib/utils';
import { updateReviewRequestStatus } from '@/lib/api/reviews';
import SentInvitationsList from './SentInvitationsList';
import { useSentInvitationsQuery } from './useSentInvitationsQuery';
import { useReceivedReviewRequestsQuery } from './useReceivedReviewRequestsQuery';

type Mode = 'received' | 'sent';

const ReceivedRequests = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: reviewRequests, isLoading, error } = useReceivedReviewRequestsQuery();

  const updateStatusMutation = useMutation({
    mutationFn: ({ requestId, status }: { requestId: string; status: 'accepted' | 'declined' }) =>
      updateReviewRequestStatus(requestId, status),
    onSuccess: (_, { status }) => {
      queryClient.invalidateQueries({ queryKey: ['review-requests'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      toast.success(`Review request ${status} successfully!`);
    },
    onError: () => {
      toast.error('Failed to update review request');
    },
  });

  const handleAccept = (requestId: string, paperId: string) => {
    updateStatusMutation.mutate({ requestId, status: 'accepted' });
    setTimeout(() => {
      navigate(`/review/${paperId}`);
    }, 1000);
  };

  const handleDecline = (requestId: string) => {
    updateStatusMutation.mutate({ requestId, status: 'declined' });
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader>
              <div className="h-6 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </CardHeader>
            <CardContent>
              <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-2/3"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">Error loading review requests. Please try again.</p>
      </div>
    );
  }

  if (!reviewRequests?.length) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <FileText className="h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No pending requests</h3>
          <p className="text-gray-600 text-center max-w-md">
            You don't have any pending review requests. Authors will reach out when they need your expertise.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {reviewRequests.map((request) => {
        const paper = request.paperInfo;
        const requester = request.requestorName;
        const receivedDate = new Date(request.createdAt).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        });

        return (
          <Card key={request.id}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="text-lg mb-2">
                    {paper?.title || 'Untitled Paper'}
                  </CardTitle>
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                      <User className="h-4 w-4" />
                      <span>Requested by: {requester || 'Unknown'}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      <span>Received: {receivedDate}</span>
                    </div>
                  </div>
                </div>
                <Badge variant="outline">Pending</Badge>
              </div>
            </CardHeader>

            <CardContent>
              <p className="text-gray-700 line-clamp-3 mb-4">
                {paper?.abstract || 'No abstract available'}
              </p>

              {request.message && (
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm font-medium text-gray-900 mb-1">Message from author:</p>
                  <p className="text-sm text-gray-700">{request.message}</p>
                </div>
              )}
            </CardContent>

            <CardFooter className="flex gap-2 pt-4">
              <Button
                onClick={() => handleAccept(request.id, request.paperId)}
                disabled={updateStatusMutation.isPending}
                className="flex-1"
              >
                <Check className="h-4 w-4 mr-2" />
                Accept & Start Review
              </Button>

              <Button
                variant="outline"
                onClick={() => handleDecline(request.id)}
                disabled={updateStatusMutation.isPending}
                className="text-red-600 hover:text-red-700"
              >
                <X className="h-4 w-4 mr-2" />
                Decline
              </Button>
            </CardFooter>
          </Card>
        );
      })}
    </div>
  );
};

const formatCount = (n: number) => (n > 99 ? '99+' : String(n));

interface CountBadgeProps {
  count: number;
  active: boolean;
}

const CountBadge = ({ count, active }: CountBadgeProps) => {
  if (count <= 0) return null;
  return (
    <span
      data-testid="count-badge"
      className={cn(
        'inline-flex h-5 min-w-5 px-1.5 items-center justify-center rounded-full text-[11px] font-medium',
        active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground',
      )}
    >
      {formatCount(count)}
    </span>
  );
};

const ReviewRequestsSection = () => {
  const [mode, setMode] = useState<Mode>('received');
  const { data: sentData } = useSentInvitationsQuery();
  const { data: receivedData } = useReceivedReviewRequestsQuery();
  const monthlyUsed = sentData?.monthlyUsed ?? 0;
  const monthlyLimit = sentData?.monthlyLimit ?? 20;
  const receivedCount = receivedData?.length ?? 0;
  const sentCount = sentData?.invitations?.length ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 min-h-8">
        <div className="text-sm text-muted-foreground">
          {mode === 'sent' && (
            <span>
              {monthlyUsed} of {monthlyLimit} invitations sent in the last 30 days
            </span>
          )}
        </div>
        <ToggleGroup
          type="single"
          value={mode}
          onValueChange={(v) => v && setMode(v as Mode)}
          aria-label="Switch between received and sent invitations"
          className="border rounded-md"
        >
          <ToggleGroupItem
            value="received"
            aria-label={
              receivedCount > 0 ? `Received, ${receivedCount} pending` : 'Received'
            }
            className="gap-2 px-3 h-8"
          >
            <Inbox className="h-4 w-4" aria-hidden />
            Received
            <CountBadge count={receivedCount} active={mode === 'received'} />
          </ToggleGroupItem>
          <ToggleGroupItem
            value="sent"
            aria-label={sentCount > 0 ? `Sent, ${sentCount} invitations` : 'Sent'}
            className="gap-2 px-3 h-8"
          >
            <Send className="h-4 w-4" aria-hidden />
            Sent
            <CountBadge count={sentCount} active={mode === 'sent'} />
          </ToggleGroupItem>
        </ToggleGroup>
      </div>

      {mode === 'received' ? <ReceivedRequests /> : <SentInvitationsList />}
    </div>
  );
};

export default ReviewRequestsSection;
